/**
 * Les **comptes joints** du module Produits communautaires — leur supervision,
 * leur gel et leur dégel, le décès d'un titulaire, et les parts d'une
 * dissolution restées à verser (lot 6 de `docs/PLAN_COMPTE_JOINT.md`).
 *
 * Maquette : artboard `b_produits`, puce « Comptes joints », action « Geler le
 * groupe ». Le dessin ne détaille pas la fiche d'un compte joint ; elle suit la
 * forme de celle d'une tontine, et en reprend les classes : même module, même
 * dessin.
 *
 * ## CE QUI DOIT SAUTER AUX YEUX
 *
 * Trois choses, et le serveur les range déjà en tête de la liste :
 *
 * 1. **une part de dissolution en souffrance** — restée à verser après un
 *    refus, ou gardée pour un titulaire décédé. De l'argent client qui dort sur
 *    un compte fermé, et que rien d'autre n'avise ;
 * 2. **un titulaire décédé** — une succession à suivre ;
 * 3. **un compte gelé** — une décision qui attend sa suite.
 *
 * ## DEUX GESTES SUR QUATRE NE S'EXÉCUTENT PAS PAR CE BOUTON
 *
 * Le dégel et le décès sont en double validation : le socle rend `202` avec
 * l'identifiant d'une demande, et **rien n'a changé**. L'écran dit donc
 * « demande en attente d'une seconde signature » — jamais « dégelé ». Le gel et
 * la relance des parts, à une signature, rendent le compte rendu du serveur.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  deceasedFilter,
  fetchJointAccount,
  fetchJointAccounts,
  requestJointAccountGesture,
  stuckSharesFilter,
} from '../http/joint-accounts.js';
import type {
  JointAccountDetail,
  JointAccountGesture,
  JointAccountHolder,
  JointAccountPage,
  JointAccountRow,
} from '../http/joint-accounts.js';

/** Les filtres de la liste, dans l'ordre de lecture : les alertes d'abord. */
const FILTRES: readonly (string | null)[] = [
  null,
  stuckSharesFilter,
  deceasedFilter,
  'GELE',
  'ACTIF',
  'EN_ATTENTE',
  'FERME',
];

/** La clé d'un message d'erreur, quelle que soit la forme de l'échec. */
function cleDErreur(echec: unknown): string {
  return echec instanceof ApiError ? echec.messageKey : 'errors.server';
}

/** Ce qui met une ligne en avant — la part en souffrance d'abord, comme le serveur la range. */
function alerteDe(ligne: JointAccountRow): string | undefined {
  if (ligne.stuckShares > 0) return 'mismatch';
  if (ligne.hasDeceasedHolder) return 'suspended';
  if (ligne.status === 'GELE') return 'postponed';
  return undefined;
}

/** Le nom à afficher d'un titulaire : l'état civil, sinon son nom d'affichage. */
function nomDe(titulaire: JointAccountHolder): string {
  return titulaire.firstName === null && titulaire.lastName === null
    ? titulaire.displayName
    : `${titulaire.firstName ?? ''} ${titulaire.lastName ?? ''}`.trim();
}

/** Le module de supervision des comptes joints. */
export function JointAccountsScreen(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();

  const [filtre, setFiltre] = useState<string | null>(null);
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [liste, setListe] = useState<JointAccountPage | null>(null);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  const [relecture, setRelecture] = useState(0);
  const [choisi, setChoisi] = useState<string | null>(null);
  const [fiche, setFiche] = useState<JointAccountDetail | null>(null);
  const [erreurFiche, setErreurFiche] = useState<string | null>(null);
  const [geste, setGeste] = useState<JointAccountGesture | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  const montant = useCallback(
    (mineures: number | null): string =>
      mineures === null
        ? t('jointAccounts.none')
        : t('jointAccounts.amount', {
            value: new Intl.NumberFormat(i18n.language).format(mineures),
          }),
    [i18n.language, t],
  );

  useEffect(() => {
    let vivant = true;
    fetchJointAccounts(client, filtre, recherche, page).then(
      (lue) => {
        if (!vivant) return;
        setListe(lue);
        setErreurListe(null);
      },
      (echec: unknown) => {
        if (!vivant) return;
        setListe(null);
        setErreurListe(cleDErreur(echec));
      },
    );
    return () => {
      vivant = false;
    };
  }, [client, filtre, recherche, page, relecture]);

  const relireLaFiche = useCallback(
    async (id: string): Promise<void> => {
      try {
        setFiche(await fetchJointAccount(client, id));
        setErreurFiche(null);
      } catch (echec) {
        setFiche(null);
        setErreurFiche(cleDErreur(echec));
      }
    },
    [client],
  );

  useEffect(() => {
    if (choisi === null) return;
    void relireLaFiche(choisi);
  }, [choisi, relireLaFiche]);

  /** Les champs du geste ouvert : le motif toujours, le titulaire et la pièce pour un décès. */
  const champs: readonly FormFieldDescriptor[] = useMemo(() => {
    const motif: FormFieldDescriptor = {
      name: 'reason',
      labelKey: 'jointAccounts.gesture.reason',
      control: 'text',
      placeholderKey: 'jointAccounts.gesture.reasonPlaceholder',
      required: true,
    };
    if (geste !== 'deceased' || fiche === null) return [motif];
    return [
      {
        name: 'holderId',
        labelKey: 'jointAccounts.gesture.holder',
        control: 'select',
        placeholderKey: 'jointAccounts.gesture.holderPlaceholder',
        required: true,
        options: fiche.holders
          .filter((titulaire) => titulaire.status === 'ACTIF')
          .map((titulaire) => ({ value: titulaire.holderId, label: nomDe(titulaire) })),
      },
      {
        name: 'evidence',
        labelKey: 'jointAccounts.gesture.evidence',
        control: 'text',
        placeholderKey: 'jointAccounts.gesture.evidencePlaceholder',
        required: true,
      },
      motif,
    ];
  }, [fiche, geste]);

  const demanderLeGeste = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (fiche === null || geste === null) return;
      try {
        const issue = await requestJointAccountGesture(client, {
          gesture: geste,
          jointAccountId: fiche.id,
          reason: valeurs.reason ?? '',
          payload:
            geste === 'deceased'
              ? { holderId: valeurs.holderId ?? '', evidence: valeurs.evidence ?? '' }
              : {},
        });
        setErreurGeste(null);
        setGeste(null);
        // **`outcome` est lu, pas supposé.** En double validation, le socle rend
        // 202 et l'identifiant d'une demande : rien n'a changé.
        setAvis(
          issue.outcome === 'EXECUTED'
            ? (issue.detail ?? t('jointAccounts.gesture.executed'))
            : t('jointAccounts.gesture.pending', { id: issue.approvalRequestId ?? '' }),
        );
        await relireLaFiche(fiche.id);
        setRelecture((n) => n + 1);
      } catch (echec) {
        setErreurGeste(cleDErreur(echec));
      }
    },
    [client, fiche, geste, relireLaFiche, t],
  );

  const chercher = (evenement: FormEvent<HTMLFormElement>): void => {
    evenement.preventDefault();
    setRecherche(saisie.trim() === '' ? null : saisie.trim());
    setPage(1);
  };

  const pages = liste === null ? 1 : Math.max(1, Math.ceil(liste.total / liste.pageSize));

  return (
    <section aria-label={t('jointAccounts.landmark')} className="tontines joint-accounts">
      <p className="tontines__journal-notice">{t('jointAccounts.journalNotice')}</p>

      {avis === null ? null : (
        <p className="tontines__avis" role="status">
          {avis}
        </p>
      )}

      {/* --- Le bandeau des trois alertes, sur TOUS les comptes joints --- */}
      {liste === null ? null : (
        <ul className="tontines__alerts" data-testid="joint-accounts-alerts">
          <li className="tontines__alert" data-tone={liste.stuckShareCount > 0 ? 'danger' : undefined}
              data-testid="joint-accounts-alert-stuck">
            <span className="tontines__alert-value">{liste.stuckShareCount}</span>
            <span className="tontines__alert-label">{t('jointAccounts.alerts.stuck')}</span>
          </li>
          <li className="tontines__alert" data-tone={liste.deceasedCount > 0 ? 'warning' : undefined}
              data-testid="joint-accounts-alert-deceased">
            <span className="tontines__alert-value">{liste.deceasedCount}</span>
            <span className="tontines__alert-label">{t('jointAccounts.alerts.deceased')}</span>
          </li>
          <li className="tontines__alert" data-tone={liste.frozenCount > 0 ? 'warning' : undefined}
              data-testid="joint-accounts-alert-frozen">
            <span className="tontines__alert-value">{liste.frozenCount}</span>
            <span className="tontines__alert-label">{t('jointAccounts.alerts.frozen')}</span>
          </li>
        </ul>
      )}

      <section aria-label={t('jointAccounts.listTitle')} className="tontines__panel">
        <h2 className="tontines__section-title">{t('jointAccounts.listTitle')}</h2>
        <form className="tontines__filters" onSubmit={chercher} role="search">
          <label className="tontines__meta" htmlFor="joint-accounts-search">
            {t('jointAccounts.search.label')}
          </label>
          <input
            className="form__input"
            data-testid="joint-accounts-search"
            id="joint-accounts-search"
            onChange={(evenement) => {
              setSaisie(evenement.target.value);
            }}
            placeholder={t('jointAccounts.search.placeholder')}
            type="search"
            value={saisie}
          />
          <button className="tontines__filter" type="submit">
            {t('jointAccounts.search.submit')}
          </button>
        </form>
        <div aria-label={t('jointAccounts.filtersLabel')} className="tontines__filters" role="group">
          {FILTRES.map((valeur) => (
            <button
              className="tontines__filter"
              data-chosen={filtre === valeur}
              data-testid={`joint-accounts-filter-${valeur ?? 'ALL'}`}
              key={valeur ?? 'ALL'}
              onClick={() => {
                setFiltre(valeur);
                setPage(1);
              }}
              type="button"
            >
              {t(`jointAccounts.filters.${valeur ?? 'ALL'}`)}
            </button>
          ))}
        </div>

        {erreurListe !== null ? (
          <p className="tontines__notice" role="alert">
            {t(erreurListe)}
          </p>
        ) : liste === null ? (
          <p className="tontines__meta">{t('jointAccounts.loading')}</p>
        ) : liste.jointAccounts.length === 0 ? (
          <p className="tontines__meta" data-testid="joint-accounts-empty">
            {t('jointAccounts.empty')}
          </p>
        ) : (
          <>
            <div className="tontines__table-frame">
              <table className="tontines__table" data-testid="joint-accounts-table">
                <thead>
                  <tr>
                    <th scope="col">{t('jointAccounts.columns.name')}</th>
                    <th scope="col">{t('jointAccounts.columns.initiator')}</th>
                    <th scope="col">{t('jointAccounts.columns.holders')}</th>
                    <th scope="col">{t('jointAccounts.columns.rule')}</th>
                    <th scope="col">{t('jointAccounts.columns.balance')}</th>
                    <th scope="col">{t('jointAccounts.columns.attention')}</th>
                    <th scope="col">{t('jointAccounts.columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.jointAccounts.map((ligne) => (
                    <tr data-alert={alerteDe(ligne)} data-testid={`joint-account-${ligne.id}`} key={ligne.id}>
                      <th className="tontines__name-cell" scope="row">
                        <button
                          className="tontines__open"
                          onClick={() => {
                            setChoisi(ligne.id);
                            setGeste(null);
                            setAvis(null);
                          }}
                          type="button"
                        >
                          {ligne.name}
                        </button>
                      </th>
                      <td>
                        <span className="tontines__strong">
                          {ligne.initiatorName ?? t('jointAccounts.unnamed')}
                        </span>
                        <span className="tontines__minor">{ligne.initiatorMsisdn ?? t('jointAccounts.none')}</span>
                      </td>
                      <td data-emphasis="numeric">{ligne.activeHolders}</td>
                      <td>{t(`jointAccounts.ruleKind.${ligne.ruleKind}`)}</td>
                      <td data-emphasis="numeric">{montant(ligne.ledgerBalance)}</td>
                      <td>
                        {/* **Ce qui demande une décision se dit par un MOT.** La teinte ne fait que doubler. */}
                        {ligne.stuckShares > 0 ? (
                          <span className="tontines__minor" data-tone="danger">
                            {t('jointAccounts.attention.stuck', { count: ligne.stuckShares })}
                          </span>
                        ) : null}
                        {ligne.hasDeceasedHolder ? (
                          <span className="tontines__minor" data-tone="warning">
                            {t('jointAccounts.attention.deceased')}
                          </span>
                        ) : null}
                        {ligne.pendingRequests > 0 ? (
                          <span className="tontines__minor">
                            {t('jointAccounts.attention.requests', { count: ligne.pendingRequests })}
                          </span>
                        ) : null}
                        {ligne.unpaidFees > 0 ? (
                          <span className="tontines__minor" data-tone="warning">
                            {t('jointAccounts.attention.unpaidFees', { count: ligne.unpaidFees })}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <span className="tontines__status"
                              data-tone={ligne.status === 'GELE' ? 'warning' : undefined}>
                          {t(`jointAccounts.status.${ligne.status}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tontines__pages">
              <button
                className="tontines__filter"
                disabled={page <= 1}
                onClick={() => {
                  setPage(page - 1);
                }}
                type="button"
              >
                {t('jointAccounts.previous')}
              </button>
              <span className="tontines__meta" data-testid="joint-accounts-page">
                {t('jointAccounts.pageOf', { page, pages, total: liste.total })}
              </span>
              <button
                className="tontines__filter"
                disabled={page >= pages}
                onClick={() => {
                  setPage(page + 1);
                }}
                type="button"
              >
                {t('jointAccounts.next')}
              </button>
            </div>
          </>
        )}
      </section>

      {/* --- La fiche --- */}
      {choisi === null ? null : erreurFiche !== null ? (
        <p className="tontines__notice" role="alert">
          {t(erreurFiche)}
        </p>
      ) : fiche === null ? (
        <p className="tontines__meta">{t('jointAccounts.loading')}</p>
      ) : (
        <JointAccountSheet
          champs={champs}
          erreurGeste={erreurGeste}
          fiche={fiche}
          geste={geste}
          montant={montant}
          onCancel={() => {
            setGeste(null);
            setErreurGeste(null);
          }}
          onOpen={(ouvert) => {
            setGeste(ouvert);
            setErreurGeste(null);
            setAvis(null);
          }}
          onSubmit={demanderLeGeste}
        />
      )}
    </section>
  );
}

/** Le décès est-il offert ? Un titulaire actif, sur un compte qui garde encore quelque chose. */
function decesOffert(fiche: JointAccountDetail): boolean {
  const vivant = fiche.holders.some((titulaire) => titulaire.status === 'ACTIF');
  if (!vivant) return false;
  if (fiche.status !== 'FERME') return true;
  // Fermé : seule une part encore à verser a un sens — la part d'un titulaire
  // qui l'attendait, et qui ne doit plus partir vers son compte personnel.
  return fiche.shares.some((part) => part.status === 'PREVUE' && !part.reservedForSuccession);
}

/** La fiche d'un compte joint. */
function JointAccountSheet({
  champs,
  erreurGeste,
  fiche,
  geste,
  montant,
  onCancel,
  onOpen,
  onSubmit,
}: {
  readonly champs: readonly FormFieldDescriptor[];
  readonly erreurGeste: string | null;
  readonly fiche: JointAccountDetail;
  readonly geste: JointAccountGesture | null;
  readonly montant: (mineures: number | null) => string;
  readonly onCancel: () => void;
  readonly onOpen: (geste: JointAccountGesture) => void;
  readonly onSubmit: (valeurs: Readonly<Record<string, string>>) => Promise<void>;
}): ReactNode {
  const { t } = useTranslation();
  const noms = new Map(fiche.holders.map((titulaire) => [titulaire.holderId, nomDe(titulaire)]));

  return (
    <section aria-label={t('jointAccounts.sheet.landmark', { name: fiche.name })}
             className="tontines__panel" data-testid="joint-account-sheet">
      <div className="tontines__sheet-head">
        <h2 className="tontines__section-title">{fiche.name}</h2>
        <span className="tontines__status" data-tone={fiche.status === 'GELE' ? 'warning' : undefined}>
          {t(`jointAccounts.status.${fiche.status}`)}
        </span>
        {fiche.freezable ? (
          <button className="tontines__danger" data-testid="joint-account-freeze"
                  onClick={() => { onOpen('freeze'); }} type="button">
            {t('jointAccounts.gesture.freeze.open')}
          </button>
        ) : null}
        {fiche.unfreezable ? (
          <button className="tontines__filter" data-testid="joint-account-unfreeze"
                  onClick={() => { onOpen('unfreeze'); }} type="button">
            {t('jointAccounts.gesture.unfreeze.open')}
          </button>
        ) : null}
        {decesOffert(fiche) ? (
          <button className="tontines__danger" data-testid="joint-account-deceased"
                  onClick={() => { onOpen('deceased'); }} type="button">
            {t('jointAccounts.gesture.deceased.open')}
          </button>
        ) : null}
        {fiche.sharesRetryable ? (
          <button className="tontines__filter" data-testid="joint-account-retry"
                  onClick={() => { onOpen('retryShares'); }} type="button">
            {t('jointAccounts.gesture.retryShares.open')}
          </button>
        ) : null}
      </div>
      <p className="tontines__meta">
        {t('jointAccounts.sheet.rule', { rule: regle(fiche, t, montant) })}
      </p>
      {fiche.status === 'GELE' && !fiche.unfreezable ? (
        <p className="tontines__notice" data-testid="joint-account-succession" role="alert">
          {t('jointAccounts.sheet.succession')}
        </p>
      ) : null}

      {geste === null ? null : (
        <div className="tontines__dissolve-form" data-testid="joint-account-gesture-form">
          <h3 className="tontines__subtitle">{t(`jointAccounts.gesture.${geste}.title`, { name: fiche.name })}</h3>
          <p className="tontines__meta">{t(`jointAccounts.gesture.${geste}.rule`)}</p>
          {erreurGeste === null ? null : (
            <p className="tontines__notice" role="alert">
              {t(erreurGeste)}
            </p>
          )}
          <FormShell fields={champs} layout="stacked" onSubmit={onSubmit}
                     submitLabelKey={`jointAccounts.gesture.${geste}.submit`} />
          <button className="tontines__filter" onClick={onCancel} type="button">
            {t('jointAccounts.gesture.cancel')}
          </button>
        </div>
      )}

      {/* --- Le sous-compte --- */}
      <div className="tontines__balance" data-testid="joint-account-balance">
        <h3 className="tontines__subtitle">{t('jointAccounts.balance.title')}</h3>
        <dl className="tontines__facts">
          <dt>{t('jointAccounts.balance.account')}</dt>
          <dd>{fiche.accountNumber ?? t('jointAccounts.none')}</dd>
          <dt>{t('jointAccounts.balance.ledger')}</dt>
          <dd data-emphasis="numeric">{montant(fiche.ledgerBalance)}</dd>
          <dt>{t('jointAccounts.balance.unpaidFees')}</dt>
          <dd data-emphasis="numeric">{montant(fiche.unpaidFeesDue)}</dd>
        </dl>
      </div>

      {/* --- Les titulaires --- */}
      <h3 className="tontines__subtitle">{t('jointAccounts.holders.title', { count: fiche.holders.length })}</h3>
      <div className="tontines__table-frame">
        <table className="tontines__table" data-testid="joint-account-holders">
          <thead>
            <tr>
              <th scope="col">{t('jointAccounts.holders.identity')}</th>
              <th scope="col">{t('jointAccounts.holders.msisdn')}</th>
              <th scope="col">{t('jointAccounts.holders.status')}</th>
              <th scope="col">{t('jointAccounts.holders.contributed')}</th>
              <th scope="col">{t('jointAccounts.holders.received')}</th>
              <th scope="col">{t('jointAccounts.holders.net')}</th>
              <th scope="col">{t('jointAccounts.holders.share')}</th>
            </tr>
          </thead>
          <tbody>
            {fiche.holders.map((titulaire) => (
              <tr data-alert={titulaire.status === 'DECEDE' ? 'suspended' : undefined}
                  data-testid={`holder-${titulaire.holderId}`} key={titulaire.holderId}>
                <th className="tontines__name-cell" scope="row">
                  <span className="tontines__strong">{nomDe(titulaire)}</span>
                  <span className="tontines__minor">
                    {titulaire.role === 'INITIATEUR'
                      ? t('jointAccounts.holders.initiator', { ref: titulaire.publicRef ?? '' })
                      : (titulaire.publicRef ?? '')}
                  </span>
                </th>
                <td>{titulaire.msisdn ?? t('jointAccounts.none')}</td>
                <td>
                  <span className="tontines__minor"
                        data-tone={titulaire.status === 'DECEDE' ? 'warning' : undefined}>
                    {t(`jointAccounts.holderStatus.${titulaire.status}`)}
                  </span>
                </td>
                <td data-emphasis="numeric">{montant(titulaire.contributedTotal)}</td>
                <td data-emphasis="numeric">{montant(titulaire.receivedTotal)}</td>
                <td data-emphasis="numeric">{montant(titulaire.net)}</td>
                <td data-emphasis="numeric">{montant(titulaire.estimatedShare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- Les demandes, closes comprises --- */}
      <h3 className="tontines__subtitle">{t('jointAccounts.requests.title', { count: fiche.requests.length })}</h3>
      {fiche.requests.length === 0 ? (
        <p className="tontines__meta">{t('jointAccounts.requests.empty')}</p>
      ) : (
        <div className="tontines__table-frame">
          <table className="tontines__table" data-testid="joint-account-requests">
            <thead>
              <tr>
                <th scope="col">{t('jointAccounts.requests.kind')}</th>
                <th scope="col">{t('jointAccounts.requests.requester')}</th>
                <th scope="col">{t('jointAccounts.requests.amount')}</th>
                <th scope="col">{t('jointAccounts.requests.signatures')}</th>
                <th scope="col">{t('jointAccounts.requests.status')}</th>
                <th scope="col">{t('jointAccounts.requests.createdAt')}</th>
              </tr>
            </thead>
            <tbody>
              {fiche.requests.map((demande) => (
                <tr data-testid={`request-${demande.id}`} key={demande.id}>
                  <td>{t(`jointAccounts.requestKind.${demande.kind}`)}</td>
                  <td>{demande.requesterName ?? t('jointAccounts.none')}</td>
                  <td data-emphasis="numeric">{montant(demande.amount)}</td>
                  <td data-emphasis="numeric">
                    {t('jointAccounts.requests.signatureCount', {
                      signed: demande.signed,
                      required: demande.requiredSignatures,
                      refused: demande.refused,
                    })}
                  </td>
                  <td>
                    {t(`jointAccounts.requestStatus.${demande.status}`)}
                    {demande.failureReason === null ? null : (
                      <span className="tontines__minor" data-tone="danger">{demande.failureReason}</span>
                    )}
                  </td>
                  <td>{demande.createdAt ?? t('jointAccounts.none')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Les frais de tenue --- */}
      <h3 className="tontines__subtitle">{t('jointAccounts.fees.title')}</h3>
      {fiche.fees.length === 0 ? (
        <p className="tontines__meta">{t('jointAccounts.fees.empty')}</p>
      ) : (
        <div className="tontines__table-frame">
          <table className="tontines__table" data-testid="joint-account-fees">
            <thead>
              <tr>
                <th scope="col">{t('jointAccounts.fees.month')}</th>
                <th scope="col">{t('jointAccounts.fees.dueOn')}</th>
                <th scope="col">{t('jointAccounts.fees.status')}</th>
                <th scope="col">{t('jointAccounts.fees.amount')}</th>
                <th scope="col">{t('jointAccounts.fees.attempts')}</th>
              </tr>
            </thead>
            <tbody>
              {fiche.fees.map((echeance) => (
                <tr data-testid={`fee-${echeance.id}`} key={echeance.id}>
                  <td>{echeance.month}</td>
                  <td>{echeance.dueOn}</td>
                  <td>
                    <span className="tontines__minor"
                          data-tone={echeance.status === 'IMPAYEE' ? 'warning' : undefined}>
                      {t(`jointAccounts.feeStatus.${echeance.status}`)}
                    </span>
                  </td>
                  <td data-emphasis="numeric">{montant(echeance.amount)}</td>
                  <td data-emphasis="numeric">{echeance.attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Les parts d'une dissolution --- */}
      <h3 className="tontines__subtitle">{t('jointAccounts.shares.title')}</h3>
      {fiche.shares.length === 0 ? (
        <p className="tontines__meta">{t('jointAccounts.shares.empty')}</p>
      ) : (
        <div className="tontines__table-frame">
          <table className="tontines__table" data-testid="joint-account-shares">
            <thead>
              <tr>
                <th scope="col">{t('jointAccounts.shares.holder')}</th>
                <th scope="col">{t('jointAccounts.shares.amount')}</th>
                <th scope="col">{t('jointAccounts.shares.status')}</th>
                <th scope="col">{t('jointAccounts.shares.attempts')}</th>
                <th scope="col">{t('jointAccounts.shares.lastFailure')}</th>
              </tr>
            </thead>
            <tbody>
              {fiche.shares.map((part) => (
                <tr data-alert={part.stuck ? 'mismatch' : undefined}
                    data-testid={`share-${part.id}`} key={part.id}>
                  <td>{part.holderName ?? noms.get(part.holderId) ?? t('jointAccounts.none')}</td>
                  <td data-emphasis="numeric">{montant(part.amount)}</td>
                  <td>
                    {/* **La souffrance se dit par un MOT**, et la succession aussi. */}
                    <span className="tontines__minor" data-tone={part.stuck ? 'danger' : undefined}>
                      {part.reservedForSuccession
                        ? t('jointAccounts.shareStatus.SUCCESSION')
                        : part.stuck
                          ? t('jointAccounts.shareStatus.STUCK')
                          : t(`jointAccounts.shareStatus.${part.status}`)}
                    </span>
                  </td>
                  <td data-emphasis="numeric">{part.attempts}</td>
                  <td>{part.lastFailure ?? t('jointAccounts.none')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Les décisions du back-office --- */}
      <h3 className="tontines__subtitle">{t('jointAccounts.decisions.title')}</h3>
      {fiche.decisions.length === 0 ? (
        <p className="tontines__meta">{t('jointAccounts.decisions.empty')}</p>
      ) : (
        <div className="tontines__table-frame">
          <table className="tontines__table" data-testid="joint-account-decisions">
            <thead>
              <tr>
                <th scope="col">{t('jointAccounts.decisions.at')}</th>
                <th scope="col">{t('jointAccounts.decisions.kind')}</th>
                <th scope="col">{t('jointAccounts.decisions.reason')}</th>
                <th scope="col">{t('jointAccounts.decisions.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {fiche.decisions.map((decision, rang) => (
                <tr key={`${decision.kind}-${String(rang)}`}>
                  <td>{decision.occurredAt ?? t('jointAccounts.none')}</td>
                  <td>
                    {decision.holderName === null
                      ? t(`jointAccounts.decisionKind.${decision.kind}`)
                      : t('jointAccounts.decisions.kindForHolder', {
                          kind: t(`jointAccounts.decisionKind.${decision.kind}`),
                          name: decision.holderName,
                        })}
                  </td>
                  <td>{decision.reason}</td>
                  <td>{decision.evidence ?? t('jointAccounts.none')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** La règle de signature, composée ici (`EX-CLI-014`) depuis son genre, son seuil et son quorum. */
function regle(
  fiche: JointAccountDetail,
  t: (cle: string, valeurs?: Record<string, unknown>) => string,
  montant: (mineures: number | null) => string,
): string {
  const { kind, quorum, threshold } = fiche.rule;
  return t(`jointAccounts.ruleText.${kind}`, {
    quorum: quorum ?? '',
    threshold: montant(threshold),
  });
}
